import React, { useState, useRef, useEffect } from 'react'
import { useApp } from '../components/app-context'
import { ChannelDayRow } from '../components/channel-day-row'
import { Channel } from '../types'
import { ProgramDrawer } from '../components/program-drawer'
import { ProgramGridHeader } from '../components/program-grid-components/program-grid-header'
import { FormMessages } from '@ddg-frontend/inputs'
import { Link, useParams } from 'react-router-dom'
import { setMetaTag } from '../utils/setMetaTag'
import { getResource } from '../utils'
import { useIsInView } from '@ddg-frontend/fe-essentials'

export const ChannelPage = () => {
  const { id } = useParams<{ id: string }>()
  const [dataTouched, setDataTouched] = useState(false)
  const {
    days,
    timeline,
    channelData,
    setTouched,
    initTimeline,
    setOffsetProgrammaticChannel,
  } = useApp()

  const { offsetX, dateDisplayed, touched } = timeline
  const channelParam = id || 'RTP1'
  const channel =
    channelData?.find((channel) => channel.callLetter === channelParam) ||
    channelData?.[0]
  const daysInterval = days.length
  const [currentInfo, setCurrentInfo] = useState<{
    ref: React.RefObject<HTMLLIElement | null>
    idx: number
  } | null>(null)

  const [selectedInfo, setSelectedInfo] = useState<
    | {
        programId: number | undefined
        channel: Channel | undefined
        date: string | undefined
      }
    | undefined
  >(undefined)

  useEffect(() => {
    if (channel) {
      document.title = `${channel.title} - Guia TV`

      const description =
        channel.description || `Programação do canal ${channel.title}`
      setMetaTag('meta[name="description"]', description)
      setMetaTag('meta[property="og:title"]', `${channel.title} - Guia TV`)
      setMetaTag('meta[property="og:description"]', description)
    }
  }, [channel])

  useEffect(() => {
    setOffsetProgrammaticChannel(0)
    scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }, [])

  const onProgramClick = (
    id: number | undefined,
    channel: Channel | undefined,
    date: string | undefined = undefined
  ) => {
    if (id) {
      const url = new URL(window.location.href)
      url.searchParams.set('programId', String(id))
      url.searchParams.set('channel', channel?.callLetter || '')
      url.searchParams.set('date', date || '')
      window.history.pushState({}, '', url.toString())
    } else {
      const url = new URL(window.location.href)
      url.searchParams.delete('programId')
      url.searchParams.delete('channel')
      url.searchParams.delete('date')
      window.history.pushState({}, '', url.toString())
    }
    setSelectedInfo({
      programId: id,
      channel: channel,
      date: date,
    })
  }

  const programGridRef = useRef<HTMLDivElement>(null)

  const scrollAmount = window.innerWidth * 0.5
  const [viewRef, isInView] = useIsInView<HTMLUListElement>({
    root: programGridRef.current,
    threshold: 0.001,
  })
  const handleScrollLeft = () => {
    if (programGridRef.current) {
      setOffsetProgrammaticChannel(offsetX - 1)
      programGridRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      })
    }
  }
  const handleScrollRight = () => {
    if (programGridRef.current) {
      setOffsetProgrammaticChannel(offsetX + 1)
      programGridRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      })
    }
  }

  const currentDate = new Date()
  const currentTimeString = currentDate.toLocaleTimeString('pt-PT', {
    hour: '2-digit',
    minute: '2-digit',
  })
  useEffect(() => {
    if (
      dateDisplayed.toISOString().slice(0, 10) !==
      new Date().toISOString().slice(0, 10)
    )
      setDataTouched(true)
  }, [dateDisplayed])
  if (channel?.isAdult) {
    return (
      <div className="channel-page adult-content">
        <ProgramGridHeader />
        <FormMessages
          type="error"
          title="Conteúdo Adulto"
          content={'Não é permitido o acesso a este conteúdo.'}
        />
      </div>
    )
  }
  return (
    <>
      <div id="channel-page" className="channel-page">
        <div
          className="slide-control"
          style={{
            opacity: isInView ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
          }}
        >
          <button
            type="button"
            onClick={handleScrollLeft}
            className="icon-seta-left"
          >
            <span className="screen-readers-only">
              Deslizar janela para a direita
            </span>
          </button>

          <button
            onClick={() => {
              initTimeline(0, new Date(new Date().toISOString().slice(0, 10)))
              setDataTouched(false)
              setTouched(false)
              const card = currentInfo?.ref?.current?.querySelectorAll(
                '.program-card'
              )[currentInfo.idx] as HTMLElement
              if (card) {
                card.scrollIntoView({
                  inline: 'center',
                  block: 'nearest',
                  behavior: 'smooth',
                })
              }
            }}
            className="time-indicator"
            type="button"
          >
            {dataTouched || touched || offsetX !== 0 ? (
              <span className="icon-relogio" />
            ) : (
              <>
                <span>{getResource('ChannelPage.Now')}</span>
                <span>{currentTimeString}</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleScrollRight}
            className="icon-seta-right"
          >
            <span className="screen-readers-only">
              Deslizar janela para a esquerda
            </span>
          </button>
        </div>
        <Link
          to="/"
          className="btn-color-1 btn-text icon-seta-left btn-icon-left"
        >
          {getResource('TVGuide')}
        </Link>
        <ProgramGridHeader />

        {!currentInfo && <div className="block-loading" />}

        <div
          className={`channel-day-container ${
            !currentInfo ? 'is-loading' : ''
          }`}
          ref={programGridRef}
          onTouchEnd={() => setTouched(true)}
        >
          <ul className={`channel-day-row`} ref={viewRef}>
            {channel &&
              days.map((day, idx) => {
                return (
                  <ChannelDayRow
                    key={idx}
                    channel={channel}
                    day={day}
                    daysInterval={daysInterval}
                    onProgramClick={onProgramClick}
                    setCurrentInfo={setCurrentInfo}
                    dateInView={dateDisplayed}
                  />
                )
              })}
          </ul>
        </div>

        <ProgramDrawer
          programId={selectedInfo?.programId}
          onProgramClick={onProgramClick}
          channel={selectedInfo?.channel}
          date={selectedInfo?.date}
        />
      </div>
    </>
  )
}
